// ─── formatMetric — the single display-side source of truth for the chat charts/tables ──────
//
// The DISPLAY-SIDE counterpart to the backend unit/scale contract (app.column_semantics):
// the backend emits a COMPUTED rate (CTR/CVR/VTR/bounce/open/win rate, share) as the RAW 0-1
// FRACTION tagged 'percent'; an UPLOADED %-origin column stays 'percent_scaled' (already points);
// ROAS/ROI are 'ratio' (Nx); money is 'currency'; counts/measures 'number'/'int'. This helper is
// the ONE place that turns a (value, format-key) pair into a display string — every consumer in
// AIInsightsPage.jsx (table cells, source-records, PDF export body, the combo line labels, the
// YAxis tick formatters, the Tooltip, the single-measure axis/tooltip, the pie label) routes
// through it, so a 'percent' fraction is ×100'd EXACTLY ONCE and all surfaces agree.
//
// Why this exists: the table dumped a raw 16-decimal fraction (0.003200316963416755) for a
// 'percent'/'ratio' cell, and the combo tooltip/label rounded a sub-1% CTR (0.0032 → 0.32) to
// '0%' while the right axis auto-scaled to a nonsensical ~400% domain. The fix is adaptive
// precision so a small rate stays visible (0.0032 → '0.32%', never '0%' or '32%'), with every
// currently-correct rendering (currency / number / date / ratio / percent_scaled) preserved.

// Compact a magnitude for an axis tick: 80000 → "80k", 1_200_000 → "1.2M", 950 → "950".
// (Kept in lock-step with AIInsightsPage._compactNum so chart axes render identically.)
export function compactNum(n) {
  const a = Math.abs(n);
  let div = 1, suf = "";
  if (a >= 1e9) { div = 1e9; suf = "B"; }
  else if (a >= 1e6) { div = 1e6; suf = "M"; }
  else if (a >= 1e3) { div = 1e3; suf = "k"; }
  const val = n / div;
  return val.toFixed(val % 1 === 0 ? 0 : 1) + suf;
}

const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function _looksLikeDate(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?)?$/.test(v.trim());
}
function _friendlyDate(v) {
  const m = String(v).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(v);
  const mi = parseInt(m[2], 10) - 1;
  if (mi < 0 || mi > 11) return String(v);
  return `${_MONTHS[mi]} ${parseInt(m[3], 10)}, ${m[1]}`;
}

// Parse a possibly-formatted numeric value (strips $ , % spaces) → Number, or null if not numeric.
// Accepts plain decimals, currency/percent-decorated strings, thousands separators, AND
// scientific notation (e.g. "8.759883768582567E-4") — a very small aggregated rate like a
// sub-0.1% CTR serializes from the backend/JSON in E-notation, and WITHOUT the exponent branch
// below the regex rejected it, _toNumber returned null, and the table dumped the raw
// "8.75...E-4" string instead of "0.09%".
function _toNumber(value) {
  if (typeof value === "number") return isFinite(value) ? value : null;
  const s = String(value).trim();
  if (!/^-?\$?\d[\d,]*\.?\d*(?:[eE][+-]?\d+)?%?$/.test(s)) return null;
  const num = parseFloat(s.replace(/[$,%]/g, ""));
  return isNaN(num) ? null : num;
}

// Thousands-separated number, at most 2 decimals (no raw full-precision float).
function _formatNumber(num) {
  const opts = Number.isInteger(num)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return num.toLocaleString("en-US", opts);
}

// $ with thousands separators; whole dollars show no cents.
function _formatCurrencyFull(num) {
  const opts = Number.isInteger(num)
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return "$" + num.toLocaleString("en-US", opts);
}

// ADAPTIVE-precision percent string from a DISPLAY-POINTS value (already ×100 for 'percent',
// as-is for 'percent_scaled'): keep a small rate visible without dumping raw decimals.
//   0        → '0%'            (and any integer → no decimals: 50 → '50%', 100 → '100%')
//   0.32     → '0.32%'         (sub-1 points → 2 decimal places so a <1% rate stays readable)
//   0.027    → '0.03%'         (rounded sensibly, never 16 decimals, never '0%')
//   72.98    → '73%'           (>= 1 points → 1 decimal, trailing '.0' stripped)
function _formatPoints(points) {
  if (!isFinite(points)) return "";
  if (points === 0) return "0%";
  if (Number.isInteger(points)) return `${points}%`;
  const a = Math.abs(points);
  // Sub-1% rates need finer precision (2 dp) so 0.32 doesn't collapse to 0%; larger values 1 dp.
  const decimals = a < 1 ? 2 : 1;
  let s = points.toFixed(decimals);
  // Trim trailing zeros ('73.0' → '73', '0.30' → '0.3') but keep a meaningful decimal.
  s = s.replace(/\.?0+$/, "");
  return `${s}%`;
}

/**
 * Turn a (value, format-key) pair into a display string — the single source of truth.
 * @param {*} value raw cell/series value (number or numeric string).
 * @param {string} format one of: currency | number | int | percent | percent_scaled | ratio | date | text.
 * @param {object} [opts]
 * @param {boolean} [opts.compact] use compact magnitudes ($1.2k / 80k) for chart axes/labels.
 * @returns {string} display string ('' for null/empty).
 */
export function formatMetric(value, format, opts = {}) {
  if (value === null || value === undefined || value === "") return "";
  const compact = !!opts.compact;

  // Non-numeric formats first.
  if (format === "date") return _looksLikeDate(value) ? _friendlyDate(value) : String(value);
  if (format === "text" || format === "id") return String(value);

  const num = _toNumber(value);

  switch (format) {
    case "currency": {
      if (num === null) return String(value);
      return compact ? `${num < 0 ? "-" : ""}$${compactNum(Math.abs(num))}` : _formatCurrencyFull(num);
    }
    case "number":
    case "int": {
      if (num === null) return String(value);
      return compact ? compactNum(num) : _formatNumber(num);
    }
    case "percent": {
      // 0-1 FRACTION → ×100 EXACTLY ONCE, then adaptive precision.
      if (num === null) return String(value);
      return _formatPoints(num * 100);
    }
    case "percent_scaled": {
      // Already in percentage POINTS (0-100) → NO ×100.
      if (num === null) return String(value);
      return _formatPoints(num);
    }
    case "ratio": {
      if (num === null) return String(value);
      return `${num.toFixed(1)}x`;
    }
    default:
      // Unknown/absent format → leave text untouched; never fabricate a '$'.
      return String(value);
  }
}

export default formatMetric;
